import React, { useState, useCallback } from 'react';
import { supabase } from '../../../supabaseClient';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Stack,
  useTheme,
  alpha,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Factory as BatchIcon,
  Engineering as LineIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as CostIcon,
  Assessment as MetricsIcon,
  Warning as DeviationIcon,
  CheckCircle as QualityIcon,
  Schedule as TimeIcon,
  Inventory as YieldIcon,
  Speed as EfficiencyIcon,
  Assignment as RequestIcon
} from '@mui/icons-material';

const ProductionData = ({ companyId }) => {
  const theme = useTheme();
  const [productionData, setProductionData] = useState({
    summary: {
      totalBatches: 0,
      totalLineRuns: 0,
      totalProductionValue: 0,
      averageYield: 0,
      activeDeviations: 0,
      pendingRequests: 0,
      standaloneBatchCount: 0,
      linkedBatchCount: 0,
      lineProductionsWithBatch: 0,
      lineProductionsWithoutBatch: 0,
      batchMetrics: {
        completed: 0,
        inProgress: 0,
        failed: 0,
        totalCost: 0,
        averageCycleTime: 0
      },
      standaloneBatchMetrics: {
        completed: 0,
        inProgress: 0,
        failed: 0,
        totalCost: 0,
        count: 0
      },
      lineMetrics: {
        completed: 0,
        inProgress: 0,
        failed: 0,
        totalCost: 0,
        averageEfficiency: 0
      }
    },
    batchAnalytics: {
      recentBatches: [],
      costAnalysis: [],
      deviations: [],
      qualityMetrics: []
    },
    lineAnalytics: {
      recentRuns: [],
      templateUsage: [],
      yields: [],
      efficiency: []
    },
    requests: {
      manufacturing: []
    }
  });

  // Pagination states
  const [batchPage, setBatchPage] = useState(0);
  const [batchRowsPerPage, setBatchRowsPerPage] = useState(10);
  const [linePage, setLinePage] = useState(0);
  const [lineRowsPerPage, setLineRowsPerPage] = useState(10);

  const fetchProductionData = useCallback(async () => {
    try {
      // Fetch batch production data
      const { data: batchesData } = await supabase
        .from('production_batches')
        .select(`
          id,
          batch_number,
          status,
          planned_quantity,
          actual_quantity,
          planned_unit_of_measure,
          calculated_production_volume,
          calculated_production_unit,
          calculated_density,
          yield_percentage,
          manufacture_date,
          total_cost,
          cost_per_unit,
          formulations!inner(
            name,
            products(name)
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      // Fetch batch costs
      const { data: batchCostsData } = await supabase
        .from('batch_costs')
        .select(`
          batch_id,
          total_cost,
          labor_cost,
          overhead_cost,
          cost_variance,
          production_batches!inner(batch_number, status)
        `)
        .order('created_at', { ascending: false });

      // Fetch batch deviations with proper company filtering
      const { data: deviationsData } = await supabase
        .from('batch_log_deviations')
        .select(`
          *,
          batch_logs!inner(
          id,
          batch_id,
            company_id,
            production_batches!inner(
              id,
              batch_number,
              lot_number,
              status,
              formulations!inner(
                id,
                name,
          company_id
              )
            )
          )
        `)
        .eq('batch_logs.production_batches.formulations.company_id', companyId)
        .order('flagged_at', { ascending: false })
        .limit(100);

      // Batch logs are now included in the deviations query above

      // Fetch batch quality control data
      const { data: qcData } = await supabase
        .from('qc_tests')
        .select(`
          id,
          test_type,
          final_status,
          test_datetime,
          batch_id,
          product_name,
          test_number,
          auto_pass_fail,
          manual_override_status,
          actual_result,
          target_value,
          min_acceptable,
          max_acceptable,
          deviation_percentage,
          tested_by_name,
          qa_review_status
        `)
        .eq('company_id', companyId)
        .order('test_datetime', { ascending: false })
        .limit(100);

      // Fetch yield summaries from batch production
      const { data: yieldData } = await supabase
        .from('yield_summary')
        .select(`
          id,
          batch_id,
          yield_percentage,
          theoretical_yield_kg,
          theoretical_yield_unit,
          actual_yield_kg,
          actual_yield_unit,
          waste_amount_kg,
          waste_amount_unit,
          final_ph,
          final_viscosity,
          final_color,
          final_texture,
          right_first_time,
          yield_comments,
          quality_notes,
          end_time,
          created_at
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      // Fetch line production data
      const { data: lineProductionData } = await supabase
        .from('line_productions')
        .select(`
          id,
          batch_number,
          batch_id,
          status,
          planned_quantity,
          actual_quantity,
          yield_percentage,
          manufacture_date,
          total_cost,
          cost_per_unit,
          template_id,
          products(name)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      // Fetch line production costs
      const { data: lineCostsData } = await supabase
        .from('production_line_costs')
        .select(`
          line_production_id,
          total_cost,
          labor_cost,
          overhead_cost,
          marketing_cost,
          cost_variance,
          total_production_batch_cost,
          line_productions!inner(batch_number, status)
        `)
        .order('created_at', { ascending: false });

      // Fetch line production yields
      const { data: lineYieldData } = await supabase
        .from('production_line_log_yield')
        .select(`
          id,
          line_log_id,
          line_production_id,
          company_id,
          total_units_produced,
          units_set_aside,
          production_end_time,
          operator_comments,
          yield_summary_completed,
          yield_summary_completed_at,
          yield_summary_completed_by,
          created_at
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(50);

      // Fetch production line templates usage
      const { data: templatesData } = await supabase
        .from('production_line_templates')
        .select(`
          id,
          template_name,
          is_active,
          products(name)
        `)
        .eq('company_id', companyId);

      // Fetch batch calendar for upcoming production
      const { data: batchCalendarData } = await supabase
        .from('batch_calendar')
        .select(`
          id,
          batch_number,
          lot_number,
          product_id,
          formulation_id,
          planned_quantity,
          planned_unit_of_measure,
          manufacture_date,
          planned_start_date,
          planned_completion_date,
          status,
          priority,
          production_location,
          production_line,
          shift_supervisor,
          created_at
        `)
        .eq('company_id', companyId)
        .gte('planned_start_date', new Date().toISOString().split('T')[0])
        .order('planned_start_date', { ascending: true })
        .limit(20);

      // Fetch manufacturing requests
      const { data: manufacturingRequests } = await supabase
        .from('manufacturing_requests')
        .select(`
          id,
          status,
          priority,
          requested_quantity,
          requested_unit,
          needed_by_date,
          reason,
          products(name),
          formulations(name),
          created_at
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(50);

      // Fetch batch signatures
      const { data: batchSignatures } = await supabase
        .from('batch_signatures')
        .select(`
          id,
          batch_id,
          role,
          signer_name,
          signer_title,
          signed_at,
          comments,
          is_required,
          created_at,
          production_batches!inner(
            batch_number,
            company_id
          )
        `)
        .eq('production_batches.company_id', companyId)
        .order('signed_at', { ascending: false })
        .limit(100);

      // Fetch line production signatures
      const { data: lineSignatures } = await supabase
        .from('production_line_log_signatures')
        .select(`
          id,
          line_production_id,
          role,
          signer_name,
          signer_title,
          signature_type,
          signed_at,
          comments,
          is_required,
          created_at,
          line_productions!inner(
            batch_number,
            company_id
          )
        `)
        .eq('line_productions.company_id', companyId)
        .order('signed_at', { ascending: false })
        .limit(100);


      // Process the data
      const processedData = processProductionData({
        batches: batchesData || [],
        batchCosts: batchCostsData || [],
        deviations: deviationsData || [],
        qcTests: qcData || [],
        yields: yieldData || [],
        lineProductions: lineProductionData || [],
        lineCosts: lineCostsData || [],
        lineYields: lineYieldData || [],
        templates: templatesData || [],
        batchCalendar: batchCalendarData || [],
        batchSignatures: batchSignatures || [],
        lineSignatures: lineSignatures || [],
        manufacturingRequests: manufacturingRequests || []
      });

      setProductionData(processedData);

    } catch (err) {
      console.error('Error fetching production data:', err);
       // Initialize empty data on error
       setProductionData({
         summary: {
           totalBatches: 0,
           totalLineRuns: 0,
           totalProductionValue: 0,
           averageYield: 0,
           activeDeviations: 0,
           pendingRequests: 0,
           standaloneBatchCount: 0,
           linkedBatchCount: 0,
           lineProductionsWithBatch: 0,
           lineProductionsWithoutBatch: 0,
           batchMetrics: {
             completed: 0,
             inProgress: 0,
             failed: 0,
             totalCost: 0,
             averageCycleTime: 0
           },
           standaloneBatchMetrics: {
             completed: 0,
             inProgress: 0,
             failed: 0,
             totalCost: 0,
             count: 0
           },
           lineMetrics: {
             completed: 0,
             inProgress: 0,
             failed: 0,
             totalCost: 0,
             averageEfficiency: 0
           }
         },
        batchAnalytics: {
          recentBatches: [],
          costAnalysis: [],
          deviations: [],
          qualityMetrics: []
        },
        lineAnalytics: {
          recentRuns: [],
          templateUsage: [],
          yields: [],
          efficiency: []
        },
        requests: {
          manufacturing: []
        }
      });
    }
  }, [companyId]);

  React.useEffect(() => {
    if (companyId) {
      fetchProductionData();
    }
  }, [companyId, fetchProductionData]);

  const processProductionData = (data) => {
    // Identify batch-line production relationships
    const linkedBatchIds = new Set(
      data.lineProductions
        .filter(lp => lp.batch_id)
        .map(lp => lp.batch_id)
    );

    // Separate standalone vs linked batches
    const standaloneBatches = data.batches.filter(b => !linkedBatchIds.has(b.id));
    const linkedBatches = data.batches.filter(b => linkedBatchIds.has(b.id));

    // Process batch data - overall counts
    const completedBatches = data.batches.filter(b => b.status === 'completed').length;
    const inProgressBatches = data.batches.filter(b => ['in_production', 'quality_testing'].includes(b.status)).length;
    const failedBatches = data.batches.filter(b => b.status === 'failed').length;
    
    // Process standalone batch data
    const completedStandaloneBatches = standaloneBatches.filter(b => b.status === 'completed').length;
    const inProgressStandaloneBatches = standaloneBatches.filter(b => ['in_production', 'quality_testing'].includes(b.status)).length;
    const failedStandaloneBatches = standaloneBatches.filter(b => b.status === 'failed').length;
    
    // Calculate standalone batch costs only (to avoid double counting)
    const standaloneBatchCosts = data.batchCosts.filter(cost => 
      standaloneBatches.some(batch => batch.id === cost.batch_id)
    );
    const totalStandaloneBatchCost = standaloneBatchCosts.reduce((sum, cost) => sum + (cost.total_cost || 0), 0);
    
    // Keep original total for reference
    const totalBatchCost = data.batchCosts.reduce((sum, cost) => sum + (cost.total_cost || 0), 0);
    
    // Calculate batch yield from yield_summary table
    const batchYieldsWithData = data.yields.filter(y => y.theoretical_yield_kg && y.actual_yield_kg);
    const averageBatchYield = batchYieldsWithData.length > 0 
      ? batchYieldsWithData.reduce((sum, y) => {
          const yieldPercent = (y.actual_yield_kg / y.theoretical_yield_kg) * 100;
          return sum + yieldPercent;
        }, 0) / batchYieldsWithData.length 
      : (data.batches.filter(b => b.yield_percentage != null).length > 0 
          ? data.batches.filter(b => b.yield_percentage != null).reduce((sum, b) => sum + (b.yield_percentage || 0), 0) / data.batches.filter(b => b.yield_percentage != null).length 
          : 0);

    // Process line production data
    const completedLines = data.lineProductions.filter(l => l.status === 'completed').length;
    const inProgressLines = data.lineProductions.filter(l => ['in_production', 'quality_testing'].includes(l.status)).length;
    const failedLines = data.lineProductions.filter(l => l.status === 'failed').length;
    
    const totalLineCost = data.lineCosts.reduce((sum, cost) => sum + (cost.total_production_batch_cost || cost.total_cost || 0), 0);
    
    // Calculate line yield from production_line_log_yield table
    const lineYieldsWithData = data.lineYields.filter(ly => ly.total_units_produced > 0);
    const averageLineYield = lineYieldsWithData.length > 0 
      ? lineYieldsWithData.reduce((sum, ly) => {
          const efficiency = ly.total_units_produced > 0 
            ? ((ly.total_units_produced - (ly.units_set_aside || 0)) / ly.total_units_produced * 100) 
            : 0;
          return sum + efficiency;
        }, 0) / lineYieldsWithData.length 
      : (data.lineProductions.length > 0 ? data.lineProductions.reduce((sum, l) => sum + (l.yield_percentage || 0), 0) / data.lineProductions.length : 0);

    // Process deviations - batch info is now included in the query
    const deviationsWithBatchInfo = data.deviations.map(deviation => {
      return {
        ...deviation,
        batchNumber: deviation.batch_logs?.production_batches?.batch_number || 'Unknown',
        batchId: deviation.batch_logs?.production_batches?.id || null,
        lotNumber: deviation.batch_logs?.production_batches?.lot_number || null,
        formulationName: deviation.batch_logs?.production_batches?.formulations?.name || 'Unknown Formulation'
      };
    });

    const activeDeviations = deviationsWithBatchInfo.filter(d => ['open', 'in_review', 'investigating', 'capa_required'].includes(d.status)).length;

    // Process requests
    const pendingManufacturingRequests = data.manufacturingRequests.filter(r => r.status === 'pending').length;

    // Process template usage
    const templateUsage = data.templates.map(template => {
      const templateLineProductions = data.lineProductions.filter(lp => lp.template_id === template.id);
      return {
        name: template.template_name,
        product: template.products?.name || 'Unknown Product',
        usageCount: templateLineProductions.length,
        activeRuns: templateLineProductions.filter(lp => lp.status === 'in_production').length,
        isActive: template.is_active
      };
    });

    // Process recent batches with enhanced data
    const recentBatches = data.batches.slice(0, 20).map(batch => {
      const batchCost = data.batchCosts.find(bc => bc.batch_id === batch.id);
      const batchYield = data.yields.find(y => y.batch_id === batch.id);
      
      // Calculate yield percentage from yield_summary if available
      let calculatedYield = batch.yield_percentage;
      if (batchYield && batchYield.theoretical_yield_kg && batchYield.actual_yield_kg) {
        calculatedYield = (batchYield.actual_yield_kg / batchYield.theoretical_yield_kg) * 100;
      }
      
      return {
        ...batch,
        yield_percentage: calculatedYield,
        totalCost: batchCost?.total_cost || batch.total_cost || 0,
        laborCost: batchCost?.labor_cost || 0,
        overheadCost: batchCost?.overhead_cost || 0,
        costVariance: batchCost?.cost_variance || 0,
        actualYield: batchYield?.actual_yield_kg || 0,
        theoreticalYield: batchYield?.theoretical_yield_kg || 0,
        rightFirstTime: batchYield?.right_first_time || false,
        finalPh: batchYield?.final_ph || null,
        finalViscosity: batchYield?.final_viscosity || null,
        wasteAmount: batchYield?.waste_amount_kg || 0,
        finalColor: batchYield?.final_color || null,
        finalTexture: batchYield?.final_texture || null,
        yieldComments: batchYield?.yield_comments || null
      };
    });

    // Process recent line runs
    const recentLineRuns = data.lineProductions.slice(0, 20).map(line => {
      const lineCost = data.lineCosts.find(lc => lc.line_production_id === line.id);
      const lineYield = data.lineYields.find(ly => ly.line_production_id === line.id);
      
       return {
         ...line,
         totalCost: lineCost?.total_production_batch_cost || lineCost?.total_cost || line.total_cost || 0,
         laborCost: lineCost?.labor_cost || 0,
         overheadCost: lineCost?.overhead_cost || 0,
         marketingCost: lineCost?.marketing_cost || 0,
         costVariance: lineCost?.cost_variance || 0,
         totalProductionBatchCost: lineCost?.total_production_batch_cost || 0,
         unitsProduced: lineYield?.total_units_produced || 0,
         unitsSetAside: lineYield?.units_set_aside || 0,
         efficiency: lineYield ? ((lineYield.total_units_produced - lineYield.units_set_aside) / lineYield.total_units_produced * 100) : 0
       };
    });

    // Process signature data
    const batchSignaturesByRole = {
      qualityControlSigned: data.batchSignatures.filter(s => s.role === 'Quality Control').length,
      productionSupervisorSigned: data.batchSignatures.filter(s => s.role === 'Production Supervisor').length,
      finalReleaseSigned: data.batchSignatures.filter(s => s.role === 'Final Release').length
    };

    const lineSignaturesByRole = {
      qualityControlSigned: data.lineSignatures.filter(s => s.role === 'Quality Control').length,
      productionSupervisorSigned: data.lineSignatures.filter(s => s.role === 'Production Supervisor').length,
      finalReleaseSigned: data.lineSignatures.filter(s => s.role === 'Final Release').length
    };

    // Combine recent signature activity from both batch and line
    const allSignatures = [
      ...data.batchSignatures.map(s => ({
        ...s,
        type: 'batch',
        identifier: s.production_batches?.batch_number || 'Unknown'
      })),
      ...data.lineSignatures.map(s => ({
        ...s,
        type: 'line',
        identifier: s.line_productions?.batch_number || 'Unknown'
      }))
    ].sort((a, b) => new Date(b.signed_at) - new Date(a.signed_at));

    return {
      summary: {
        totalBatches: data.batches.length,
        totalLineRuns: data.lineProductions.length,
        totalProductionValue: totalStandaloneBatchCost + totalLineCost, // No double counting
        averageYield: (averageBatchYield + averageLineYield) / 2,
        activeDeviations,
        pendingRequests: pendingManufacturingRequests,
        // Production relationship insights
        standaloneBatchCount: standaloneBatches.length,
        linkedBatchCount: linkedBatches.length,
        lineProductionsWithBatch: data.lineProductions.filter(lp => lp.batch_id).length,
        lineProductionsWithoutBatch: data.lineProductions.filter(lp => !lp.batch_id).length,
        batchMetrics: {
          completed: completedBatches,
          inProgress: inProgressBatches,
          failed: failedBatches,
          totalCost: totalBatchCost, // Keep for reference
          averageCycleTime: 0 // Could be calculated from batch logs
        },
        standaloneBatchMetrics: {
          completed: completedStandaloneBatches,
          inProgress: inProgressStandaloneBatches,
          failed: failedStandaloneBatches,
          totalCost: totalStandaloneBatchCost,
          count: standaloneBatches.length
        },
        lineMetrics: {
          completed: completedLines,
          inProgress: inProgressLines,
          failed: failedLines,
          totalCost: totalLineCost,
          averageEfficiency: averageLineYield
        }
      },
      batchAnalytics: {
        recentBatches,
        costAnalysis: data.batchCosts.slice(0, 10),
        deviations: deviationsWithBatchInfo.slice(0, 20),
        qualityMetrics: data.qcTests.slice(0, 10)
      },
      lineAnalytics: {
        recentRuns: recentLineRuns,
        templateUsage,
        yields: data.lineYields.slice(0, 10),
        efficiency: recentLineRuns.map(run => ({
          runNumber: run.batch_number,
          efficiency: run.efficiency,
          unitsProduced: run.unitsProduced,
          unitsSetAside: run.unitsSetAside
        }))
      },
      signatures: {
        ...batchSignaturesByRole,
        recentActivity: allSignatures.slice(0, 10)
      },
      batchCalendar: data.batchCalendar || [],
      requests: {
        manufacturing: data.manufacturingRequests
      }
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const convertVolumeToOz = (volumeInGal) => {
    if (!volumeInGal) return null;
    return volumeInGal * 128; // 1 gallon = 128 fluid ounces
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': '#4CAF50',
      'in_production': '#2196F3',
      'quality_testing': '#FF9800',
      'failed': '#F44336',
      'cancelled': '#9E9E9E',
      'draft': '#607D8B',
      'approved': '#8BC34A',
      'scheduled': '#00BCD4',
      'on_hold': '#FFC107'
    };
    return colors[status] || '#666';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'low': '#4CAF50',
      'medium': '#FF9800',
      'high': '#FF5722',
      'critical': '#F44336'
    };
    return colors[severity] || '#666';
  };

  // Pagination handlers
  const handleBatchPageChange = (event, newPage) => {
    setBatchPage(newPage);
  };

  const handleBatchRowsPerPageChange = (event) => {
    setBatchRowsPerPage(parseInt(event.target.value, 10));
    setBatchPage(0);
  };

  const handleLinePageChange = (event, newPage) => {
    setLinePage(newPage);
  };

  const handleLineRowsPerPageChange = (event) => {
    setLineRowsPerPage(parseInt(event.target.value, 10));
    setLinePage(0);
  };

  return (
    <Box sx={{ mb: 6 }}>
       {/* Summary Cards */}
       <Grid container spacing={3} sx={{ mb: 4 }}>
         
         <Grid item xs={12} sm={6} lg={4}>
           <Card sx={{ 
             borderRadius: 3,
             border: `1px solid ${theme.palette.grey[400]}`,
             background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)',
             boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.12)}`,
             transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
             '&:hover': {
               transform: 'translateY(-4px)',
               boxShadow: `0 12px 32px ${alpha(theme.palette.info.main, 0.18)}`
             }
           }}>
             <CardContent sx={{ p: 3 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                 <Box sx={{ 
                   p: 1.5, 
                   borderRadius: 2, 
                   bgcolor: alpha(theme.palette.info.main, 0.1),
                   mr: 2
                 }}>
                   <BatchIcon sx={{ fontSize: 24, color: 'info.main' }} />
                 </Box>
                 <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                   Standalone Batches
                 </Typography>
               </Box>
               <Typography variant="h4" sx={{ 
                 fontWeight: 500, 
                 color: 'info.main',
                 mb: 1
               }}>
                 {formatCurrency(productionData.summary?.standaloneBatchMetrics?.totalCost || 0)}
               </Typography>
               <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                 {productionData.summary?.standaloneBatchCount || 0} Independent Batches
               </Typography>
             </CardContent>
           </Card>
         </Grid>

         <Grid item xs={12} sm={6} lg={4}>
           <Card sx={{ 
             borderRadius: 3,
             border: `1px solid ${theme.palette.grey[400]}`,
             background: 'linear-gradient(135deg, #fce4ec 0%, #ffffff 100%)',
             boxShadow: `0 8px 24px ${alpha('#E91E63', 0.12)}`,
             transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
             '&:hover': {
               transform: 'translateY(-4px)',
               boxShadow: `0 12px 32px ${alpha('#E91E63', 0.18)}`
             }
           }}>
             <CardContent sx={{ p: 3 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                 <Box sx={{ 
                   p: 1.5, 
                   borderRadius: 2, 
                   bgcolor: alpha('#E91E63', 0.1),
                   mr: 2
                 }}>
                   <LineIcon sx={{ fontSize: 24, color: '#E91E63' }} />
                 </Box>
                 <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                   Line Production Value
                 </Typography>
               </Box>
               <Typography variant="h4" sx={{ 
                 fontWeight: 500, 
                 color: '#E91E63',
                 mb: 1
               }}>
                 {formatCurrency(productionData.summary?.lineMetrics?.totalCost || 0)}
               </Typography>
               <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                 Includes Linked Batch Costs
               </Typography>
             </CardContent>
           </Card>
         </Grid>

         <Grid item xs={12} sm={6} lg={4}>
           <Card sx={{ 
             borderRadius: 3,
             border: `1px solid ${theme.palette.grey[400]}`,
             background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
             boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
             transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
             '&:hover': {
               transform: 'translateY(-4px)',
               boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
             }
           }}>
             <CardContent sx={{ p: 3 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                 <Box sx={{ 
                   p: 1.5, 
                   borderRadius: 2, 
                   bgcolor: alpha(theme.palette.warning.main, 0.1),
                   mr: 2
                 }}>
                   <DeviationIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                 </Box>
                 <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                   Active Deviations
                 </Typography>
               </Box>
               <Typography variant="h4" sx={{ 
                 fontWeight: 500, 
                 color: 'warning.main',
                 mb: 1
               }}>
                 {formatNumber(productionData.summary?.activeDeviations || 0)}
               </Typography>
               <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                 Require attention
               </Typography>
             </CardContent>
           </Card>
         </Grid>
       </Grid>

      {/* Production Metrics Breakdown */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Batch Production Metrics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 500, 
                      color: '#4CAF50',
                      mb: 1
                    }}>
                      {formatNumber(productionData.summary.batchMetrics.completed)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Completed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 500, 
                      color: '#2196F3',
                      mb: 1
                    }}>
                      {formatNumber(productionData.summary.batchMetrics.inProgress)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      In Progress
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Total Cost:
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 500 }}>
                      {formatCurrency(productionData.summary.batchMetrics.totalCost)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Line Production Metrics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 500, 
                      color: '#4CAF50',
                      mb: 1
                    }}>
                      {formatNumber(productionData.summary.lineMetrics.completed)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Completed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 500, 
                      color: '#2196F3',
                      mb: 1
                    }}>
                      {formatNumber(productionData.summary.lineMetrics.inProgress)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      In Progress
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Total Cost:
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 500 }}>
                      {formatCurrency(productionData.summary.lineMetrics.totalCost)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cost Analysis Breakdown */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Batch Cost Breakdown Analysis
              </Typography>
              {productionData.batchAnalytics.costAnalysis.length > 0 ? (
                <Box>
                  {productionData.batchAnalytics.costAnalysis.slice(0, 5).map((cost, index) => {
                    const totalCost = cost.total_cost || 0;
                    const laborPercentage = totalCost > 0 ? ((cost.labor_cost || 0) / totalCost * 100) : 0;
                    const overheadPercentage = totalCost > 0 ? ((cost.overhead_cost || 0) / totalCost * 100) : 0;
                    const materialPercentage = 100 - laborPercentage - overheadPercentage;
                    
                    return (
                      <Box key={index} sx={{ 
                        p: 3,
                        mb: 2,
                        borderRadius: 2,
                        bgcolor: alpha('#4CAF50', 0.05),
                        border: `1px solid ${alpha('#4CAF50', 0.2)}`,
                        cursor: 'default',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha('#4CAF50', 0.08),
                          border: `1px solid ${alpha('#4CAF50', 0.3)}`,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha('#4CAF50', 0.15)}`
                        }
                      }}>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              Batch Number: {cost.production_batches?.batch_number || 'Unknown Batch'}
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 500 }}>
                              {formatCurrency(totalCost)}
                            </Typography>
                          </Box>

                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#2196F3' }}>
                                {materialPercentage.toFixed(1)}%
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Materials
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#FF9800' }}>
                                {laborPercentage.toFixed(1)}%
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Labor
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#9C27B0' }}>
                                {overheadPercentage.toFixed(1)}%
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Overhead
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No cost analysis data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Production Template Performance */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Production Template Performance
              </Typography>
              {productionData.lineAnalytics.templateUsage.length > 0 ? (
                <Box>
                  {productionData.lineAnalytics.templateUsage.slice(0, 6).map((template, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 2.5,
                      mb: 1.5,
                      borderRadius: 2,
                      bgcolor: template.isActive ? alpha('#4CAF50', 0.05) : alpha('#9E9E9E', 0.05),
                      border: `1px solid ${alpha(template.isActive ? '#4CAF50' : '#9E9E9E', 0.2)}`,
                      cursor: 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: template.isActive ? alpha('#4CAF50', 0.08) : alpha('#9E9E9E', 0.08),
                        border: `1px solid ${alpha(template.isActive ? '#4CAF50' : '#9E9E9E', 0.3)}`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(template.isActive ? '#4CAF50' : '#9E9E9E', 0.15)}`
                      }
                    }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {template.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {template.product} • {template.usageCount} runs total
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 500, 
                          color: template.activeRuns > 0 ? '#2196F3' : 'text.secondary',
                          mb: 0.5
                        }}>
                          {template.activeRuns}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Active Runs
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No template data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quality Control Insights */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Quality Control Test Results
              </Typography>
              {productionData.batchAnalytics.qualityMetrics.length > 0 ? (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#4CAF50', 0.1) }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, color: '#4CAF50' }}>
                          {productionData.batchAnalytics.qualityMetrics.filter(qc => qc.final_status === 'PASS').length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Passed
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#F44336', 0.1) }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, color: '#F44336' }}>
                          {productionData.batchAnalytics.qualityMetrics.filter(qc => qc.final_status === 'FAIL').length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Failed
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#FF9800', 0.1) }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, color: '#FF9800' }}>
                          {productionData.batchAnalytics.qualityMetrics.filter(qc => qc.final_status === 'REVIEW').length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Under Review
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#9E9E9E', 0.1) }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, color: '#9E9E9E' }}>
                          {productionData.batchAnalytics.qualityMetrics.filter(qc => qc.final_status === 'PENDING').length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Pending
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {/* Recent QC Tests */}
                  {productionData.batchAnalytics.qualityMetrics.slice(0, 5).map((qc, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 2,
                      mb: 1,
                      borderRadius: 2,
                      bgcolor: alpha(qc.final_status === 'PASS' ? '#4CAF50' : qc.final_status === 'FAIL' ? '#F44336' : '#FF9800', 0.05),
                      border: `1px solid ${alpha(qc.final_status === 'PASS' ? '#4CAF50' : qc.final_status === 'FAIL' ? '#F44336' : '#FF9800', 0.2)}`,
                      cursor: 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(qc.final_status === 'PASS' ? '#4CAF50' : qc.final_status === 'FAIL' ? '#F44336' : '#FF9800', 0.08),
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(qc.final_status === 'PASS' ? '#4CAF50' : qc.final_status === 'FAIL' ? '#F44336' : '#FF9800', 0.15)}`
                      }
                    }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {qc.test_type || 'QC Test'} - {qc.test_number || 'No Test #'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {qc.product_name || 'Unknown Product'} • Tested by: {qc.tested_by_name || 'Unknown'}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip 
                          label={qc.final_status || 'PENDING'}
                          size="small"
                          sx={{ 
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderRadius: 2,
                            color: 'white',
                            bgcolor: qc.final_status === 'PASS' ? '#4CAF50' : qc.final_status === 'FAIL' ? '#F44336' : '#FF9800',
                            mb: 0.5
                          }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {formatDate(qc.test_datetime)}
                        </Typography>
                        {qc.actual_result && qc.target_value && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            Result: {qc.actual_result} (Target: {qc.target_value})
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No quality control data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Yield Performance Analysis */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Yield Performance
              </Typography>
              
              {/* Average Yield */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Average Yield
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 500, 
                    color: productionData.summary.averageYield >= 95 ? '#4CAF50' : productionData.summary.averageYield >= 85 ? '#FF9800' : '#F44336'
                  }}>
                    {productionData.summary.averageYield.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={productionData.summary.averageYield} 
                  sx={{ 
                    height: 8,
                    borderRadius: 4,
                    bgcolor: alpha('#E0E0E0', 0.3),
                    '& .MuiLinearProgress-bar': {
                      bgcolor: productionData.summary.averageYield >= 95 ? '#4CAF50' : productionData.summary.averageYield >= 85 ? '#FF9800' : '#F44336',
                      borderRadius: 4
                    }
                  }}
                />
              </Box>

              {/* Batch vs Line Yield Comparison */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                  Yield by Production Type
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#2196F3', 0.1) }}>
                      <Typography variant="h5" sx={{ fontWeight: 500, color: '#2196F3' }}>
                        {(() => {
                          const batchYieldsWithData = productionData.batchAnalytics.recentBatches.filter(b => b.yield_percentage != null);
                          return batchYieldsWithData.length > 0 
                            ? (batchYieldsWithData.reduce((sum, b) => sum + (b.yield_percentage || 0), 0) / batchYieldsWithData.length).toFixed(1)
                            : '0.0';
                        })()}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Batch Production
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#E91E63', 0.1) }}>
                      <Typography variant="h5" sx={{ fontWeight: 500, color: '#E91E63' }}>
                        {(() => {
                          const lineYieldsWithData = productionData.lineAnalytics.recentRuns.filter(r => r.efficiency != null && r.efficiency > 0);
                          return lineYieldsWithData.length > 0 
                            ? (lineYieldsWithData.reduce((sum, r) => sum + (r.efficiency || 0), 0) / lineYieldsWithData.length).toFixed(1)
                            : '0.0';
                        })()}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Line Production
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Right First Time Rate */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Right First Time Rate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 500, 
                    color: '#4CAF50'
                  }}>
                    {(() => {
                      const rightFirstTimeCount = productionData.batchAnalytics.recentBatches.filter(b => b.rightFirstTime === true).length;
                      const totalWithYieldData = productionData.batchAnalytics.recentBatches.filter(b => b.rightFirstTime != null).length;
                      return rightFirstTimeCount;
                    })()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    / {productionData.batchAnalytics.recentBatches.filter(b => b.rightFirstTime != null).length || productionData.batchAnalytics.recentBatches.length} batches
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Production Calendar & Planning Insights */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Production Schedule Overview
              </Typography>
              
              {/* Upcoming Batches from Calendar */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: '#2196F3' }}>
                  Scheduled Batches (Next 7 Days)
                </Typography>
                {productionData.batchCalendar && productionData.batchCalendar.length > 0 ? (
                  <Box>
                    {productionData.batchCalendar.slice(0, 4).map((batch, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 2.5,
                        mb: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(batch.priority === 'urgent' ? '#F44336' : batch.priority === 'high' ? '#FF9800' : '#2196F3', 0.05),
                        border: `1px solid ${alpha(batch.priority === 'urgent' ? '#F44336' : batch.priority === 'high' ? '#FF9800' : '#2196F3', 0.2)}`,
                        cursor: 'default',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(batch.priority === 'urgent' ? '#F44336' : batch.priority === 'high' ? '#FF9800' : '#2196F3', 0.08),
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(batch.priority === 'urgent' ? '#F44336' : batch.priority === 'high' ? '#FF9800' : '#2196F3', 0.15)}`
                        }
                      }}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                            {batch.batch_number}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {formatNumber(batch.planned_quantity)} {batch.planned_unit_of_measure} • {batch.production_location || 'No location'}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Chip 
                            label={batch.priority}
                            size="small"
                            sx={{ 
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              borderRadius: 2,
                              color: 'white',
                              bgcolor: batch.priority === 'urgent' ? '#F44336' : batch.priority === 'high' ? '#FF9800' : '#2196F3',
                              mb: 0.5
                            }}
                          />
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {formatDate(batch.planned_start_date)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ 
                    p: 3, 
                    borderRadius: 2, 
                    bgcolor: alpha('#2196F3', 0.05),
                    border: `1px solid ${alpha('#2196F3', 0.2)}`
                  }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                      No scheduled batches in the next 7 days
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Production Efficiency Trends */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: '#FF9800' }}>
                  Production Efficiency Trends
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#4CAF50', 0.1) }}>
                      <Typography variant="h5" sx={{ fontWeight: 500, color: '#4CAF50' }}>
                        {productionData.summary.batchMetrics.completed + productionData.summary.lineMetrics.completed}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Completed This Month
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#2196F3', 0.1) }}>
                      <Typography variant="h5" sx={{ fontWeight: 500, color: '#2196F3' }}>
                        {productionData.summary.batchMetrics.inProgress + productionData.summary.lineMetrics.inProgress}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Currently Running
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#F44336', 0.1) }}>
                      <Typography variant="h5" sx={{ fontWeight: 500, color: '#F44336' }}>
                        {productionData.summary.batchMetrics.failed + productionData.summary.lineMetrics.failed}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Failed/Cancelled
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Cost Variance Analysis */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: '#9C27B0' }}>
                  Cost Variance Analysis
                </Typography>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  bgcolor: alpha('#9C27B0', 0.05),
                  border: `1px solid ${alpha('#9C27B0', 0.2)}`
                }}>
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                        Average Material Cost
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#2196F3' }}>
                        {formatCurrency(productionData.batchAnalytics.costAnalysis.reduce((sum, c) => sum + (c.total_cost || 0), 0) / Math.max(productionData.batchAnalytics.costAnalysis.length, 1) * 0.6)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                        Total Cost Variance
                      </Typography>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 500, 
                        color: productionData.batchAnalytics.costAnalysis.reduce((sum, c) => sum + (c.cost_variance || 0), 0) > 0 ? '#F44336' : '#4CAF50'
                      }}>
                        {formatCurrency(productionData.batchAnalytics.costAnalysis.reduce((sum, c) => sum + (c.cost_variance || 0), 0))}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Critical Production Alerts */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Critical Production Alerts
              </Typography>
              
              {/* Critical Deviations */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: '#F44336' }}>
                  Critical Deviations
                </Typography>
                {productionData.batchAnalytics.deviations.filter(d => 
                  (d.issue_severity === 'critical' || d.severity_level === 'Critical') && ['open', 'in_review', 'investigating', 'capa_required'].includes(d.status)
                ).slice(0, 3).map((deviation, index) => (
                  <Box key={`critical-${index}`} sx={{ 
                    p: 2,
                    mb: 1,
                    borderRadius: 2,
                    bgcolor: alpha('#F44336', 0.1),
                    border: `1px solid ${alpha('#F44336', 0.3)}`,
                    cursor: 'default'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#F44336', mb: 0.5 }}>
                      {(deviation.issue_type || deviation.deviation_type || 'Unknown Issue').replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Batch: {deviation.batchNumber} • Lot: {deviation.lotNumber || 'N/A'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Flagged by: {deviation.flagged_by_role || 'Unknown'} • {formatDate(deviation.flagged_at || deviation.occurred_at)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Status: {deviation.status} • Severity: {deviation.issue_severity || deviation.severity_level}
                    </Typography>
                  </Box>
                ))}
                
                {productionData.batchAnalytics.deviations.filter(d => 
                  (d.issue_severity === 'critical' || d.severity_level === 'Critical') && ['open', 'in_review', 'investigating', 'capa_required'].includes(d.status)
                ).length === 0 && (
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha('#4CAF50', 0.1),
                    border: `1px solid ${alpha('#4CAF50', 0.2)}`
                  }}>
                    <Typography variant="body2" sx={{ color: '#4CAF50', textAlign: 'center' }}>
                      No critical deviations
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* CAPA Issues */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: '#FF5722' }}>
                  CAPA Issues
                </Typography>
                {productionData.batchAnalytics.deviations.filter(d => 
                  (d.requires_capa === true || d.capa_required === true) && ['open', 'in_review', 'capa_required', 'investigating'].includes(d.status)
                ).slice(0, 3).map((deviation, index) => (
                  <Box key={`capa-${index}`} sx={{ 
                    p: 2,
                    mb: 1,
                    borderRadius: 2,
                    bgcolor: alpha('#FF5722', 0.1),
                    border: `1px solid ${alpha('#FF5722', 0.3)}`,
                    cursor: 'default'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#FF5722', mb: 0.5 }}>
                      {(deviation.issue_type || deviation.deviation_type || 'Unknown Issue').replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Batch: {deviation.batchNumber} • Lot: {deviation.lotNumber || 'N/A'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Severity: {deviation.issue_severity || deviation.severity_level} • Flagged: {formatDate(deviation.flagged_at || deviation.occurred_at)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Investigation: {deviation.investigation_started_at ? 'Started' : 'Not started'} • Status: {deviation.status}
                    </Typography>
                  </Box>
                ))}
                
                {productionData.batchAnalytics.deviations.filter(d => 
                  (d.requires_capa === true || d.capa_required === true) && ['open', 'in_review', 'capa_required', 'investigating'].includes(d.status)
                ).length === 0 && (
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha('#4CAF50', 0.1),
                    border: `1px solid ${alpha('#4CAF50', 0.2)}`
                  }}>
                    <Typography variant="body2" sx={{ color: '#4CAF50', textAlign: 'center' }}>
                      No CAPA issues
                    </Typography>
                  </Box>
                )}
              </Box>

            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Production Data Tables */}
      <Grid container spacing={4}>
        {/* Recent Batches */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Recent Production Batches
              </Typography>
              {productionData.batchAnalytics.recentBatches.length > 0 ? (
                <Box>
                  <TableContainer sx={{ 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    overflow: 'hidden'
                  }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Batch Number
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Product
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Quantity
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Status
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Yield %
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Total Cost
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {productionData.batchAnalytics.recentBatches
                          .slice(batchPage * batchRowsPerPage, batchPage * batchRowsPerPage + batchRowsPerPage)
                          .map((batch, index) => (
                          <TableRow key={index} sx={{
                            cursor: 'default',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              transition: 'all 0.2s ease',
                              boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2,
                              px: 2.5
                            }
                          }}>
                            <TableCell>
                              <Typography variant="body1" sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.95rem'
                              }}>
                                {batch.batch_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.9rem'
                              }}>
                                {batch.formulations?.products?.name || 'Unknown Product'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.9rem'
                              }}>
                                {batch.calculated_production_volume ? 
                                  `${formatNumber(batch.calculated_production_volume)} ${batch.calculated_production_unit || 'gal'}` : 
                                  (batch.planned_quantity ? `${formatNumber(batch.planned_quantity)} ${batch.planned_unit_of_measure || ''}` : '—')
                                }
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={batch.status}
                                size="small"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  borderRadius: 2,
                                  color: 'white',
                                  bgcolor: getStatusColor(batch.status)
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: batch.yield_percentage >= 95 ? 'success.main' : batch.yield_percentage >= 85 ? 'warning.main' : 'error.main',
                                fontSize: '0.9rem'
                              }}>
                                {batch.yield_percentage ? `${batch.yield_percentage.toFixed(1)}%` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: 'success.main',
                                fontSize: '0.9rem'
                              }}>
                                {batch.totalCost ? formatCurrency(batch.totalCost) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.9rem'
                              }}>
                                {formatDate(batch.manufacture_date)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {productionData.batchAnalytics.recentBatches.length > 10 && (
                    <Box sx={{ 
                      borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      borderRadius: '0 0 12px 12px'
                    }}>
                      <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={productionData.batchAnalytics.recentBatches.length}
                        rowsPerPage={batchRowsPerPage}
                        page={batchPage}
                        onPageChange={handleBatchPageChange}
                        onRowsPerPageChange={handleBatchRowsPerPageChange}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No batch production data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Line Productions */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Recent Line Productions
              </Typography>
              {productionData.lineAnalytics.recentRuns.length > 0 ? (
                <Box>
                  <TableContainer sx={{ 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    overflow: 'hidden'
                  }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid #E91E63`,
                            color: '#E91E63',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Batch Number
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid #E91E63`,
                            color: '#E91E63',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Product
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid #E91E63`,
                            color: '#E91E63',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Status
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid #E91E63`,
                            color: '#E91E63',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Units Produced
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid #E91E63`,
                            color: '#E91E63',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Efficiency %
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid #E91E63`,
                            color: '#E91E63',
                            py: 1.5,
                            px: 2.5
                          }}>
                            Total Cost
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {productionData.lineAnalytics.recentRuns
                          .slice(linePage * lineRowsPerPage, linePage * lineRowsPerPage + lineRowsPerPage)
                          .map((run, index) => (
                          <TableRow key={index} sx={{
                            cursor: 'default',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              transition: 'all 0.2s ease',
                              boxShadow: `inset 4px 0 0 #E91E63`
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2,
                              px: 2.5
                            }
                          }}>
                            <TableCell>
                              <Typography variant="body1" sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.95rem'
                              }}>
                                {run.batch_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.9rem'
                              }}>
                                {run.products?.name || 'Unknown Product'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={run.status}
                                size="small"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  borderRadius: 2,
                                  color: 'white',
                                  bgcolor: getStatusColor(run.status)
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.9rem'
                              }}>
                                {formatNumber(run.unitsProduced)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: run.efficiency >= 95 ? 'success.main' : run.efficiency >= 85 ? 'warning.main' : 'error.main',
                                fontSize: '0.9rem'
                              }}>
                                {run.efficiency ? `${run.efficiency.toFixed(1)}%` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: 'success.main',
                                fontSize: '0.9rem'
                              }}>
                                {run.totalCost ? formatCurrency(run.totalCost) : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {productionData.lineAnalytics.recentRuns.length > 10 && (
                    <Box sx={{ 
                      borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: `linear-gradient(135deg, ${alpha('#E91E63', 0.05)}, ${alpha('#E91E63', 0.02)})`,
                      borderRadius: '0 0 12px 12px'
                    }}>
                      <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={productionData.lineAnalytics.recentRuns.length}
                        rowsPerPage={lineRowsPerPage}
                        page={linePage}
                        onPageChange={handleLinePageChange}
                        onRowsPerPageChange={handleLineRowsPerPageChange}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No line production data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Batch Signatures & Approvals Status - Full Width Row */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            mt: 5,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Batch Approval Status
              </Typography>
              
              <Grid container spacing={3}>
                {/* Approval Status Overview */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                {/* Quality Control Row */}
                    <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: alpha('#4CAF50', 0.05),
                  border: `1px solid ${alpha('#4CAF50', 0.2)}`,
                        textAlign: 'center',
                  cursor: 'default',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha('#4CAF50', 0.08),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha('#4CAF50', 0.15)}`
                  }
                }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, color: '#4CAF50', mb: 1 }}>
                          {productionData.signatures?.qualityControlSigned || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Quality Control Approvals
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Signed
                    </Typography>
                  </Box>
                    </Grid>

                {/* Production Supervisor Row */}
                    <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: alpha('#FF9800', 0.05),
                  border: `1px solid ${alpha('#FF9800', 0.2)}`,
                        textAlign: 'center',
                  cursor: 'default',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha('#FF9800', 0.08),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha('#FF9800', 0.15)}`
                  }
                }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, color: '#FF9800', mb: 1 }}>
                          {productionData.signatures?.productionSupervisorSigned || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Production Supervisor Approvals
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Signed
                    </Typography>
                  </Box>
                    </Grid>

                {/* Final Release Row */}
                    <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: alpha('#2196F3', 0.05),
                  border: `1px solid ${alpha('#2196F3', 0.2)}`,
                        textAlign: 'center',
                  cursor: 'default',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha('#2196F3', 0.08),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha('#2196F3', 0.15)}`
                  }
                }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, color: '#2196F3', mb: 1 }}>
                          {productionData.signatures?.finalReleaseSigned || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Final Release Approvals
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Signed
                    </Typography>
                  </Box>
                    </Grid>
                  </Grid>
                </Grid>

              {/* Recent Signature Activity */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 3,
                    borderRadius: 2,
                    bgcolor: alpha('#9C27B0', 0.02),
                    border: `1px solid ${alpha('#9C27B0', 0.1)}`,
                    height: 'fit-content'
                  }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: '#9C27B0' }}>
                  Recent Signature Activity
                </Typography>
                {productionData.signatures?.recentActivity && productionData.signatures.recentActivity.length > 0 ? (
                      <Stack spacing={1.5}>
                        {productionData.signatures.recentActivity.slice(0, 3).map((signature, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: alpha(signature.type === 'batch' ? '#9C27B0' : '#E91E63', 0.08),
                            border: `1px solid ${alpha(signature.type === 'batch' ? '#9C27B0' : '#E91E63', 0.15)}`,
                      cursor: 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                              bgcolor: alpha(signature.type === 'batch' ? '#9C27B0' : '#E91E63', 0.12),
                        transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(signature.type === 'batch' ? '#9C27B0' : '#E91E63', 0.2)}`
                            }
                          }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ 
                                color: signature.type === 'batch' ? '#9C27B0' : '#E91E63',
                                mb: 0.3
                              }}>
                                {signature.role}
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: 'text.secondary',
                                display: 'block'
                              }}>
                                {signature.identifier}
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.7rem'
                              }}>
                                by {signature.signer_name}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="caption" sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.7rem'
                              }}>
                                {formatDate(signature.signed_at)}
                              </Typography>
                            </Box>
                    </Box>
                        ))}
                      </Stack>
                ) : (
                  <Box sx={{ 
                        p: 3,
                        textAlign: 'center',
                    borderRadius: 2, 
                        bgcolor: alpha('#9E9E9E', 0.05),
                        border: `1px dashed ${alpha('#9E9E9E', 0.3)}`
                  }}>
                        <Typography variant="body2" sx={{ 
                          color: 'text.secondary',
                          fontStyle: 'italic'
                        }}>
                      No recent signature activity
                    </Typography>
                  </Box>
                )}
              </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        </Grid>

      {/* Production Requests Row */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        {/* Manufacturing Requests */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Manufacturing Requests
              </Typography>
              
              {/* Status Summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#FF9800', 0.1) }}>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: '#FF9800' }}>
                      {productionData.requests.manufacturing.filter(r => r.status === 'pending').length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Pending
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#F44336', 0.1) }}>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: '#F44336' }}>
                      {productionData.requests.manufacturing.filter(r => r.status === 'rejected').length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Rejected
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#4CAF50', 0.1) }}>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: '#4CAF50' }}>
                      {productionData.requests.manufacturing.filter(r => r.status === 'approved').length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Approved
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Recent Manufacturing Requests */}
                <Box>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: '#2196F3' }}>
                  Recent Requests
                </Typography>
                {productionData.requests.manufacturing.slice(0, 4).map((request, index) => (
                    <Box key={`mfg-${index}`} sx={{ 
                      p: 2,
                      mb: 1,
                      borderRadius: 2,
                    bgcolor: alpha('#2196F3', 0.05),
                    border: `1px solid ${alpha('#2196F3', 0.2)}`,
                      cursor: 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                      bgcolor: alpha('#2196F3', 0.08),
                        transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha('#2196F3', 0.15)}`
                      }
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {request.products?.name || request.formulations?.name || 'Unknown Product'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Qty: {formatNumber(request.requested_quantity)} • {request.reason}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip 
                          label={request.status}
                          size="small"
                          sx={{ 
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderRadius: 2,
                            mb: 0.5,
                            bgcolor: request.status === 'pending' ? '#FF9800' : request.status === 'rejected' ? '#F44336' : request.status === 'approved' ? '#4CAF50' : request.status === 'in_progress' ? '#2196F3' : '#9E9E9E',
                            color: 'white'
                          }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {request.needed_by_date ? formatDate(request.needed_by_date) : 'No deadline'}
                        </Typography>
                      </Box>
                      </Box>
                    </Box>
                  ))}
                
                {productionData.requests.manufacturing.length === 0 && (
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha('#9E9E9E', 0.1),
                    border: `1px solid ${alpha('#9E9E9E', 0.2)}`
                  }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                      No manufacturing requests
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductionData;